import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import CustomInput from "../../custom/custom-input/CustomInput";
import CustomSelect from "../../custom/custom-select";
import FilterDatePicker from "../../custom/сustom-filter-date/FilterDatePicker";
import CustomInputWithUnits from "../../custom/custom-input-with-units/CustomInputWithUnits";
import CustomUploadFileMultiple from "../../orders-aviation/components/custom-upload/CustomUploadFileMultiple";
import CargoInsurance from "../../orders-new/cargo-insurance/CargoInsurance";
import SimpleCategoryModal from "../add-new-category/SimpleCategoryModal";
import { WEIGHT_UNITS } from "../../../mock";
import { INCOTERMS_CODES } from "../../../pages/orders/utils/newOrderPayload";
import { CreationCard, CreationToggle } from "./OrderCreationControls";
import OrderCreationGroupIcon from "../../shared/icons/OrderCreationGroupIcon";
import OrderCreationInfoIcon from "../../shared/icons/OrderCreationInfoIcon";
import styles from "./UnifiedOrderCreation.module.scss";
import RailwayOrderFields from "./RailwayOrderFields";

const INCOTERMS = INCOTERMS_CODES.map((code) => ({ id: code, name: code }));

export default function UnifiedOrderDetails(p) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [categorySearch, setCategorySearch] = useState(null);
  const vehiclesCountId = useId();
  const { errors = {} } = p;
  const isInternational = Boolean(
    p.countryFrom?.id && p.countryTo?.id && p.countryFrom.id !== p.countryTo.id,
  );
  const field = (name, label, placeholder, inputType = "text") => (
    <CustomInput
      labelText={label}
      placeholder={placeholder}
      inputType={inputType}
      isBgGray
      value={p[name]}
      onChangeValue={p[`set${name[0].toUpperCase()}${name.slice(1)}`]}
      errorText={errors[name]}
    />
  );
  const categoryField = (
    <CustomSelect
      labelText={`${t("ordersCreateDesktop.detailsSection.categoryLabel")}*`}
      placeholder={t("ordersCreateDesktop.detailsSection.categoryPlaceholder")}
      options={p.categories}
      defaultValue={p.category}
      returnObject
      onSubmit={p.setCategory}
      errorText={errors.category}
      showAddCategoryButton
      onOptionsUpdate={p.setCategories}
      addCategoryButtonText={t("bulkCreate.fields.addCategory")}
      onAddCategoryClick={(search) => setCategorySearch(search || "")}
    />
  );

  return (
    <CreationCard title={t("ordersCreateDesktop.detailsSection.title")} counter={3 + p.points.length}>
      {p.isRailway ? (
        <RailwayOrderFields {...p} categoryField={categoryField} />
      ) : (
        <div className={styles.fields}>
          <CustomSelect
            labelText={`${t("ordersCreateDesktop.detailsSection.transportsTypeLabel")}*`}
            placeholder={t("ordersCreateDesktop.detailsSection.transportTypePlaceholder")}
            options={p.types}
            defaultValue={p.type}
            iconKey="icon"
            returnObject
            errorText={errors.type}
            onSubmit={(type) => {
              p.setType(type);
              if (type?.value === "awning") p.setTemperature("");
            }}
          />
          {categoryField}
          <CustomInput
            labelText={t("ordersCreateDesktop.detailsSection.temperatureLabel")}
            placeholder={t("unifiedOrder.temperaturePlaceholder")}
            isBgGray
            value={p.temperature}
            onChangeValue={p.setTemperature}
            errorText={errors.temperature}
            isDisabled={p.type?.value === "awning"}
          />
          <CustomInputWithUnits
            labelText={t("unifiedOrder.weightLabel")}
            isBgGray
            inputType="number"
            value={p.weight}
            onChangeValue={p.setWeight}
            errorText={errors.weight}
            units={WEIGHT_UNITS(t)}
            selectedUnit={p.weightUnit.key}
            onChangeUnit={p.setWeightUnit}
          />
          {field("pallet", t("unifiedOrder.palletLabel"), t("unifiedOrder.quantityPlaceholder"), "number")}
          <CustomInput
            labelText={t("ordersCreateDesktop.detailsSection.volumeLabel")}
            isBgGray
            inputType="number"
            value={p.volume}
            onChangeValue={p.setVolume}
            errorText={errors.volume}
            prefixEnd={
              <span>
                {t("ordersCreateDesktop.detailsSection.volumeUnit")}
                <sup>3</sup>
              </span>
            }
          />
          {field("tnvedCode", t("bulkCreate.fields.tnved"), t("unifiedOrder.tnvedPlaceholder"))}
          <CustomSelect
            labelText={t("ordersCreateDesktop.detailsSection.loadingTypeLabel")}
            placeholder={t("unifiedOrder.loadingPlaceholder")}
            options={p.loadingTypes}
            defaultValue={p.loadingType}
            returnObject
            onSubmit={p.setLoadingType}
          />
        </div>
      )}
      {field("details", t("comment.label"), t("comment.placeholder"))}
      <div className={styles.options}>
        <CustomUploadFileMultiple
          user={user}
          files={p.files}
          onUploadFiles={p.setFiles}
          serverFiles={p.serverFiles}
          onDeleteServerFile={p.onDeleteServerFile}
          deferServerFileDeletion={p.deferServerFileDeletion}
          title={t("unifiedOrder.documents")}
        />
        {!p.isRailway && <CargoInsurance
          isReadOnly={p.isEdit}
          defaultValue={p.insurance}
          onClick={p.queryInsurance}
          declaredPrice={p.declaredPrice}
          setDeclaredPrice={p.setDeclaredPrice}
          fileInvoice={p.fileInvoice}
          setInvoiceFile={p.setInvoiceFile}
          errorInsurance={errors.declaredPrice}
          errorFileInvoice={errors.fileInvoice}
          renderHeader={({ onChange, disabled }) => (
            <div className={styles.insuranceHeader}>
              <CreationToggle
                title={t("unifiedOrder.insurance")}
                description={t("cargoInsurance.insuranceCostInfo3")}
                icon={OrderCreationInfoIcon}
                checked={p.insurance}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
          )}
        />}
        {isInternational && (
          <div className={styles.optionSection}>
            <CreationToggle
              title={t("unifiedOrder.incotermsTitle")}
              description={t("unifiedOrder.incotermsDescription")}
              icon={OrderCreationInfoIcon}
              checked={p.incotermsEnabled}
              onChange={p.setIncotermsEnabled}
            />
            {p.incotermsEnabled && (
              <CustomSelect
                labelText={t("unifiedOrder.incotermsLabel")}
                placeholder={t("unifiedOrder.incotermsPlaceholder")}
                options={INCOTERMS}
                defaultValue={INCOTERMS.find((item) => item.id === p.incoterms)}
                onSubmit={p.setIncoterms}
                errorText={errors.incoterms}
              />
            )}
          </div>
        )}
        <div className={styles.groupSection}>
          <CreationToggle
            title={t("unifiedOrder.groupTitle")}
            description={t(p.isRailway ? "railwayOrder.groupDescription" : "unifiedOrder.groupDescription")}
            icon={OrderCreationGroupIcon}
            checked={p.isComplexTender}
            onChange={p.setIsComplexTender}
            disabled={p.isEdit || p.assignmentMethod === "DirectAssignment"}
          />
          {p.isComplexTender && (
            <div className={styles.groupFields}>
              <FilterDatePicker
                date={[p.pickUpDate, p.shippingDateTo]}
                minDate={p.minPickDate}
                isEditable
                isRange
                setDate={([shippingDate, shippingDateTo]) => {
                  p.setPickUpDate(shippingDate);
                  p.setShippingDateTo(shippingDateTo);
                }}
                labelText={t("unifiedOrder.loadingWindowLabel")}
                placeholderText={t("unifiedOrder.loadingWindowPlaceholder")}
                errorDate={errors.pickUpDate || errors.shippingDateTo}
              />
              <div className={styles.counterField}>
                <div className={styles.counter}>
                  <label className={styles.counterLabel} htmlFor={vehiclesCountId}>
                    {t(p.isRailway ? "railwayOrder.wagonsCountLabel" : "unifiedOrder.vehiclesCountLabel")}
                  </label>
                  <button
                    type="button"
                    aria-label={t(p.isRailway ? "railwayOrder.decreaseWagons" : "unifiedOrder.decreaseVehicles")}
                    disabled={p.vehiclesCount <= 1}
                    onClick={() => p.setVehiclesCount(Math.max(1, Number(p.vehiclesCount) - 1))}
                  >
                    −
                  </button>
                  <input
                    id={vehiclesCountId}
                    type="number"
                    min="1"
                    step="1"
                    aria-label={t(p.isRailway ? "railwayOrder.wagonsCount" : "unifiedOrder.vehiclesCount")}
                    aria-invalid={Boolean(errors.vehiclesCount)}
                    aria-describedby={errors.vehiclesCount ? `${vehiclesCountId}-error` : undefined}
                    value={p.vehiclesCount}
                    onChange={(event) => p.setVehiclesCount(event.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={t(p.isRailway ? "railwayOrder.increaseWagons" : "unifiedOrder.increaseVehicles")}
                    onClick={() => p.setVehiclesCount(Number(p.vehiclesCount) + 1)}
                  >
                    +
                  </button>
                </div>
                {errors.vehiclesCount && <span id={`${vehiclesCountId}-error`} className={styles.error}>{errors.vehiclesCount}</span>}
              </div>
            </div>
          )}
        </div>
      </div>
      {categorySearch !== null && (
        <SimpleCategoryModal
          initialName={categorySearch}
          onClose={() => setCategorySearch(null)}
          onCategoryAdded={(category) => {
            p.setCategories([...p.categories, category]);
            p.setCategory(category);
            setCategorySearch(null);
          }}
        />
      )}
    </CreationCard>
  );
}
